import React, { useEffect, useState } from 'react';
import { Leaf, Scale, Eye, Heart } from 'lucide-react';
import '../styles/Mission.css';

const Mission = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const principles = [
    {
      icon: <Eye size={32} />,
      title: 'Depth Over Quantity',
      description: 'Routes and activities that reveal how Madeira functions as a living territory'
    },
    {
      icon: <Leaf size={32} />,
      title: 'Natural Balance',
      description: 'Respecting natural limits and reducing pressure on overused areas'
    },
    {
      icon: <Heart size={32} />,
      title: 'Local Connection',
      description: 'Highlighting places and people often left outside traditional tourism'
    },
    {
      icon: <Scale size={32} />,
      title: 'Balance, Not Extraction',
      description: 'Creating harmony between exploration and preservation'
    }
  ];

  return (
    <div className="mission-page-narrative">
      {/* Page Header */}
      <section className="mission-hero">
        <div className="mission-hero-overlay"></div>
        <div className="container">
          <div className={`mission-hero-content ${isVisible ? 'animated fadeIn' : 'animated'}`}>
            <h1 className="display-large">Our Mission</h1>
          </div>
        </div>
      </section>

      {/* Our Mission Statement */}
      <section className="mission-statement-narrative">
        <div className="container">
          <div className="mission-content-wrap">
            <h2 className="mission-main-title">
              Our mission is to design experiences that allow travelers to understand the island rather than simply visit it.
            </h2>

            <div className="mission-text-content">
              <p>
                We focus on depth over quantity, choosing routes, activities and encounters that reveal how Madeira functions as a living territory — environmentally, culturally and socially. Each experience is shaped to respect natural limits, reduce pressure on overused areas and highlight places and people often left outside traditional tourism.
              </p>

              <div className="mission-principle-highlight">
                <p><strong>Our work is guided by a simple principle: travel should create balance, not extraction.</strong></p>
              </div>

              <p>
                Balance between exploration and preservation. Between curiosity and respect. Between the visitor and the place visited.
              </p>

              <p>
                By working with small groups and local knowledge, we aim to create journeys that feel honest, grounded and meaningful — experiences that leave a lighter footprint on the island, and a lasting impression on those who experience it.
              </p>
            </div>

            {/* Principles Grid */}
            <div className="mission-principles-grid">
              {principles.map((principle, index) => (
                <div key={index} className="mission-principle-card">
                  <div className="principle-icon">{principle.icon}</div>
                  <h3 className="principle-title">{principle.title}</h3>
                  <p className="principle-description">{principle.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters to You */}
      <section className="why-matters-section">
        <div className="container">
          <div className="why-matters-grid">
            {/* Image Column */}
            <div className="why-matters-image">
              <img 
                src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&h=800&fit=crop" 
                alt="Elvio in Madeira" 
              />
            </div>

            {/* Text Column */}
            <div className="why-matters-text">
              <h2 className="why-matters-title">Why It Matters to You</h2>

              <div className="why-matters-content">
                <p className="opening-statement">Madeira didn't need to be discovered by me.<br />What it needed was time.</p>

                <p>
                  When I came back to the island, I didn't experience it through organized tours or predefined routes. I explored it slowly, sometimes clumsily, often alone. I saw places overexposed, others forgotten. I met passionate locals — and also witnessed how tourism, when rushed or poorly designed, can disconnect people from their own land.
                </p>

                <p>
                  What struck me most wasn't what travelers were missing, but what the island was losing: silence, balance, meaning. Too many experiences felt extractive — take the view, take the photo, move on — without understanding the place, the people, or the consequences.
                </p>

                <p className="realization-text">
                  At the same time, Madeira showed me something else.<br />
                  A depth that doesn't reveal itself quickly. Stories carried by landscapes. Traditions still alive, but fragile. Nature that rewards patience and respect. I realized that this island doesn't need more tourism — it needs better tourism.
                </p>

                <p>
                  <strong>Madeira Outdoors comes from that realization.</strong><br />
                  From the desire to design experiences that are fair — for the traveler and for the island. Experiences that don't rush, don't overcrowd, and don't reduce Madeira to a highlight reel.
                </p>

                <div className="personal-statement">
                  <p>This matters to me because I live here.<br />
                  Because this is not a playground — it's home.<br />
                  And because the way we choose to travel today shapes what will still exist tomorrow.</p>
                </div>

                <p className="closing-statement">
                  If you travel with us, you're not just choosing an activity.<br />
                  You're choosing a way of engaging with a place — consciously, respectfully, and honestly.
                </p>

                <p className="signature-elvio">— Elvio</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="mission-cta-section">
        <div className="container">
          <div className="mission-cta-content">
            <h2 className="heading-2">Ready to experience Madeira differently?</h2>
            <p className="body-large">
              Join us for a journey that respects the island and transforms the traveler.
            </p>
            <div className="mission-cta-buttons">
              <a href="/custom-trip" className="btn-primary">
                Plan Your Journey
              </a>
              <a href="/experiences" className="btn-secondary">
                Explore Our Tours
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mission;
